import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus, Plus, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Unified Add Lead Dialog Component
 * Used across: CRM Page, Center Dashboard, Students Page
 * All leads saved to unified CRM database via /api/center/leads
 * Backend handles: center_id (from token), duplicate validation, lead scoring
 */
const AddLeadDialog = ({ 
  open, 
  onOpenChange, 
  onSuccess
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [leadForm, setLeadForm] = useState({
    name: '',
    phone: '',
    email: '',
    course: '',
    source: 'walk_in',
    notes: ''
  });

  const getAuthHeaders = () => {
    const token = localStorage.getItem('center_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch courses for dropdown
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const res = await axios.get(`${API}/courses`);
        setCourses(res.data || []);
      } catch (error) {
        console.log('Could not fetch courses');
      }
    };
    if (open) {
      fetchCourses();
    }
  }, [open]);

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      setLeadForm({
        name: '',
        phone: '',
        email: '',
        course: '',
        source: 'walk_in',
        notes: ''
      });
    }
  }, [open]);

  // Validate phone number (10 digits)
  const validatePhone = (phone) => {
    return /^[0-9]{10}$/.test(phone);
  };

  // Validate email format
  const validateEmail = (email) => {
    if (!email) return true; // Email is optional
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!leadForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    
    if (!leadForm.phone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    
    if (!validatePhone(leadForm.phone)) {
      toast.error('Please enter valid 10-digit phone number');
      return;
    }
    
    if (!validateEmail(leadForm.email)) {
      toast.error('Please enter valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Unified payload matching LeadCreate Pydantic model
      const payload = {
        name: leadForm.name.trim(),
        mobile: leadForm.phone.trim(),  // Backend expects 'mobile' not 'phone'
        email: leadForm.email.trim() || null,
        course_interested: leadForm.course || null,
        source: leadForm.source,
        remarks: leadForm.notes.trim() || null,  // Backend expects 'remarks' not 'notes'
        preferred_mode: "center"
      };

      const res = await axios.post(`${API}/center/leads`, payload, { 
        headers: getAuthHeaders() 
      });

      toast.success('Lead Added Successfully! / लीड जोड़ी गई!');
      onOpenChange(false);
      
      // Trigger refresh callback
      if (onSuccess) {
        onSuccess(res.data);
      }
    } catch (error) {
      console.error('Add lead error:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to add lead';
      
      // Handle duplicate error specifically
      if (errorMsg.includes('already exists')) {
        toast.error(`Duplicate Lead! ${errorMsg}`);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-emerald-600" />
            Add New Lead
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            {/* Name - Full Width */}
            <div className="col-span-2">
              <Label>Name / नाम <span className="text-red-500">*</span></Label>
              <Input
                value={leadForm.name}
                onChange={(e) => setLeadForm({...leadForm, name: e.target.value})}
                placeholder="Lead name"
                required
                data-testid="lead-name-input"
              />
            </div>
            
            {/* Phone */}
            <div>
              <Label>Phone / फोन <span className="text-red-500">*</span></Label>
              <Input
                value={leadForm.phone}
                onChange={(e) => setLeadForm({...leadForm, phone: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                placeholder="10-digit phone"
                required
                data-testid="lead-phone-input"
              />
            </div>
            
            {/* Email */}
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({...leadForm, email: e.target.value})}
                placeholder="Email address"
                data-testid="lead-email-input"
              />
            </div>
            
            {/* Source */}
            <div>
              <Label>Source / स्रोत</Label>
              <select
                value={leadForm.source}
                onChange={(e) => setLeadForm({...leadForm, source: e.target.value})}
                className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                data-testid="lead-source-select"
              >
                <option value="walk_in">Walk-in</option>
                <option value="phone_call">Phone Call</option>
                <option value="referral">Referral</option>
                <option value="social_media">Social Media</option>
                <option value="website">Website</option>
                <option value="advertisement">Advertisement</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            {/* Course Interest */}
            <div>
              <Label>Course Interest / कोर्स</Label>
              <select
                value={leadForm.course}
                onChange={(e) => setLeadForm({...leadForm, course: e.target.value})}
                className="w-full h-10 px-3 border rounded-lg bg-white text-sm"
                data-testid="lead-course-select"
              >
                <option value="">Select Course</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.name}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Notes - Full Width */}
            <div className="col-span-2">
              <Label>Notes / नोट्स</Label>
              <Input
                value={leadForm.notes}
                onChange={(e) => setLeadForm({...leadForm, notes: e.target.value})}
                placeholder="Additional notes"
                data-testid="lead-notes-input"
              />
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              data-testid="lead-cancel-btn"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting} 
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="lead-submit-btn"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Add Lead
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadDialog;
